import React, { useMemo, useRef, useState } from 'react';
import { Modal, InputGroup, Form, Button, Spinner } from 'react-bootstrap';
import { BsWhatsapp, BsClipboardCheck, BsBoxArrowUpRight, BsShareFill } from 'react-icons/bs';
import { FaPrint, FaDownload } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { shareViaWhatsApp, shareViaSystem } from '../utils/shareUtils';
import { downloadReceiptImage, withInlineAssets } from '../utils/receiptImage';
import ReceiptView from './ReceiptView';

/**
 * ShareModal - Reusable modal component for sharing receipts/documents
 * @param {boolean} show - Controls modal visibility
 * @param {function} onHide - Callback to close modal
 * @param {string} shareableLink - URL to share
 * @param {string} message - Message to share via WhatsApp/System
 * @param {string} title - Title for the share (used in navigator.share)
 * @param {string} phoneNumber - Optional phone number for WhatsApp (with country code)
 * @param {function} onPrint - Callback for print button
 * @param {function} [onPrintDeliverySlip] - Callback for delivery slip print button
 * @param {string} openButtonLabel - Optional custom label for "Open" button
 * @param {object} [receiptSale] - Full sale/quote payload. When provided, a "Download"
 *   button renders the receipt (via ReceiptView) as a PNG image.
 * @param {string} [receiptDocumentType] - 'receipt' | 'quote' for ReceiptView
 * @param {string} [receiptFileName] - Base file name for the downloaded image
 */
const ShareModal = ({
    show,
    onHide,
    shareableLink,
    message,
    title,
    phoneNumber = null,
    onPrint,
    onPrintDeliverySlip,
    openButtonLabel = null,
    receiptSale = null,
    receiptDocumentType = 'receipt',
    receiptFileName = 'recibo'
}) => {
    const { t } = useTranslation();
    const receiptRef = useRef(null);
    const [downloading, setDownloading] = useState(false);

    // Same data as ReceiptView renders on screen, but with logo/QR URLs routed
    // through the inline endpoint so they survive rasterization (CORS).
    const captureSale = useMemo(() => withInlineAssets(receiptSale), [receiptSale]);

    const handleCopyLink = () => {
        navigator.clipboard.writeText(shareableLink)
            .then(() => toast.success(t('toast.linkCopied')))
            .catch(() => toast.error(t('toast.copyFailed')));
    };

    const handleWhatsAppClick = () => {
        shareViaWhatsApp(message, phoneNumber);
    };

    const handleShareClick = () => {
        shareViaSystem(
            { title, text: message },
            () => toast.success(t('shareModal.shared')),
            () => toast.error(t('shareModal.shareFailed'))
        );
    };

    const handleDownloadClick = async () => {
        if (downloading || !receiptRef.current) return;
        setDownloading(true);
        try {
            await downloadReceiptImage(receiptRef.current, receiptFileName);
            toast.success(t('shareModal.downloadSuccess', 'Recibo descargado como imagen.'));
        } catch (error) {
            console.error('Receipt image download failed', error);
            toast.error(t('shareModal.downloadError', 'No se pudo generar la imagen del recibo.'));
        } finally {
            setDownloading(false);
        }
    };

    return (
        <Modal show={show} onHide={onHide} centered>
            <Modal.Header closeButton>
                <Modal.Title>{t('shareModal.title')}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <p className="text-muted">{t('shareModal.instructions')}</p>

                <InputGroup className="mb-3">
                    <Form.Control
                        type="text"
                        value={shareableLink}
                        readOnly
                        onClick={(e) => e.target.select()}
                    />
                    <Button variant="outline-secondary" onClick={handleCopyLink}>
                        <BsClipboardCheck size={20} />
                    </Button>
                </InputGroup>

                <div className="d-flex justify-content-center flex-wrap gap-2 mb-3">
                    <Button
                        variant="light"
                        onClick={handleWhatsAppClick}
                        className="d-flex align-items-center gap-2"
                        aria-label="WhatsApp"
                    >
                        <BsWhatsapp size={20} /> <span>WhatsApp</span>
                    </Button>

                    {/* Only show Share button on mobile devices */}
                    {/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) && (
                        <Button
                            variant="light"
                            onClick={handleShareClick}
                            className="d-flex align-items-center gap-2"
                            aria-label={t('shareModal.shareViaSystem')}
                        >
                            <BsShareFill size={20} /> <span>{t('shareModal.shareViaSystem')}</span>
                        </Button>
                    )}

                    <Button
                        variant="light"
                        onClick={onPrint}
                        className="d-flex align-items-center gap-2"
                    >
                        <FaPrint size={20} /> <span>{t('shareModal.print', 'Imprimir')}</span>
                    </Button>

                    {onPrintDeliverySlip && (
                        <Button
                            variant="light"
                            onClick={onPrintDeliverySlip}
                            className="d-flex align-items-center gap-2"
                        >
                            <span role="img" aria-label="delivery">🏍️</span> <span>{t('shareModal.printDeliverySlip', 'Ticket Domicilio')}</span>
                        </Button>
                    )}

                    {receiptSale && (
                        <Button
                            variant="light"
                            onClick={handleDownloadClick}
                            disabled={downloading}
                            className="d-flex align-items-center gap-2"
                        >
                            {downloading
                                ? <Spinner animation="border" size="sm" />
                                : <FaDownload size={20} />}
                            <span>{downloading
                                ? t('shareModal.downloading', 'Generando imagen...')
                                : t('shareModal.download', 'Descargar')}</span>
                        </Button>
                    )}

                    <Button
                        variant="light"
                        as="a"
                        href={shareableLink}
                        target="_blank"
                        className="d-flex align-items-center gap-2"
                    >
                        <BsBoxArrowUpRight size={20} />
                        <span>{openButtonLabel || t('shareModal.openInvoice')}</span>
                    </Button>
                </div>

                {/* Off-screen render of the receipt, used only as the source for the
                    downloadable image. Kept visually identical to ReceiptPage. */}
                {receiptSale && (
                    <div
                        aria-hidden="true"
                        style={{
                            position: 'fixed',
                            left: '-10000px',
                            top: 0,
                            pointerEvents: 'none',
                            zIndex: -1
                        }}
                    >
                        <div
                            ref={receiptRef}
                            style={{ width: '460px', background: '#ffffff', padding: '24px' }}
                        >
                            <ReceiptView sale={captureSale} documentType={receiptDocumentType} />
                        </div>
                    </div>
                )}
            </Modal.Body>
        </Modal>
    );
};

export default ShareModal;
